import { Injectable } from '@angular/core';
import { merge, Observable, ReplaySubject, Subject } from 'rxjs';
import { NotExecutedHistoryItem } from '../model/history-item/not-executed-history-item';
import { NotExecutedHistoryItemAsset } from '../model/history-item/not-executed-history-item-asset';
import Employee from '../model/product/employee';
import IPBoardPart from '../model/product/ip-board/ip-board-part';

@Injectable({
  providedIn: 'root',
})
export class ProjectDisplayObservableService {
  private productPhoneNumberChanged = new Subject<any>();

  public getProductPhoneNumberChangedSubscribable() {
    return this.productPhoneNumberChanged;
  }

  private designerPicked = new Subject<any>();

  public getDesignerPickedSubscribable(): Subject<any> {
    return this.designerPicked;
  }

  private productDesignVariableChanged = new Subject<any>();

  public getProductDesignVariableChangedSubscribable(): Subject<any> {
    return this.productDesignVariableChanged;
  }

  private productPrintworkVariableChanged = new Subject<any>();

  public getProductPrintworkVariableChangedSubscribable(): Subject<any> {
    return this.productPrintworkVariableChanged;
  }

  private placersChanged = new Subject<any>();

  public getPlacersChangedSubscribable(): Subject<any> {
    return this.placersChanged;
  }

  private tourConfigurationChanged =
    new ReplaySubject<TourConfigurationChangedEvent>(1);

  public getTourConfigurationChangedSubscribable(): Subject<TourConfigurationChangedEvent> {
    return this.tourConfigurationChanged;
  }

  private tourUnassignedAsSideEffect =
    new Subject<TourUnassignedAsSideEffectEvent>();

  public getTourUnassignedAsSideEffectSubscribable(): Subject<TourUnassignedAsSideEffectEvent> {
    return this.tourUnassignedAsSideEffect;
  }

  private notExecutedHistoryItemAssetsFetched =
    new Subject<NotExecutedHistoryItemAssetsFetchedEvent>();

  public getNotExecutedHistoryItemAssetsFetchedSubscribable(): Subject<NotExecutedHistoryItemAssetsFetchedEvent> {
    return this.notExecutedHistoryItemAssetsFetched;
  }

  private projectContactsChanged = new Subject<any>();

  public getProjectContactsChangedSubscribable(): Subject<any> {
    return this.projectContactsChanged;
  }

  private selectedOtherContactsChanged = new Subject<any>();

  public getSelectedOtherContactsChangedSubscribable(): Subject<any> {
    return this.selectedOtherContactsChanged;
  }

  private projectExtraCompanyContactsChanged = new Subject<any>();

  public getProjectExtraCompanyContactsChangedSubscribable(): Subject<any> {
    return this.projectExtraCompanyContactsChanged;
  }

  private projectInvoicesChanged = new Subject<any>();

  public getProjectInvoicesChangedSubscribable(): Subject<any> {
    return this.projectInvoicesChanged;
  }

  private invoicePriceChanged = new Subject<any>();

  public getProjectInvoicePriceChangedSubscribable(): Subject<any> {
    return this.invoicePriceChanged;
  }

  private productAdded = new Subject<any>();

  public getProductAddedSubscribable(): Subject<any> {
    return this.productAdded;
  }

  private productExecuted = new Subject<any>();

  public getProductExecutedSubscribable(): Subject<any> {
    return this.productExecuted;
  }

  private projectPlacementExecutedSubscribable = new Subject<number>();

  public getProjectPlacementExecutedSubscribable(): Subject<number> {
    return this.projectPlacementExecutedSubscribable;
  }

  private repoByInpressSubscribable = new Subject<number>();

  public getRepoByInpressSubscribable(): Subject<number> {
    return this.repoByInpressSubscribable;
  }

  private installedByInpressSubscribable = new Subject<number>();

  public getInstalledByInpressSubscribable(): Subject<number> {
    return this.installedByInpressSubscribable;
  }

  private projectPlacementRequestDateChanged = new Subject<any>();

  public getProjectPlacementRequestDateChangedSubscribable() {
    return this.projectPlacementRequestDateChanged;
  }

  private projectPlacementVariableChanged = new Subject<any>();

  public getProjectPlacementVariableChangedSubscribable() {
    return this.projectPlacementVariableChanged;
  }

  private productPlacementVariableChanged = new Subject<any>();

  public getProductPlacementVariableChangedSubscribable() {
    return this.productPlacementVariableChanged;
  }

  private paintedPostsChanged = new Subject<any>();

  public getPaintedPostsChangedSubscribable(): Subject<any> {
    return this.paintedPostsChanged;
  }

  private mergedProjectProductPlacementVariableChanged: Observable<any>;

  public getMergedProjectProductPlacementVariableChangedSubscribable() {
    if (this.mergedProjectProductPlacementVariableChanged == null) {
      this.mergedProjectProductPlacementVariableChanged = merge(
        this.productExecuted,
        this.projectPlacementVariableChanged
      );
    }
    return this.mergedProjectProductPlacementVariableChanged;
  }

  private projectPlacementAssetsChanged = new Subject<number | undefined>();

  public getProjectPlacementAssetsChangedSubscribable(): Subject<
    number | undefined
  > {
    return this.projectPlacementAssetsChanged;
  }

  private productAssetsChanged = new Subject<ProductAssetsChangedEvent>();

  public getProductAssetsChangedSubscribable() {
    return this.productAssetsChanged;
  }

  private subProjectDesignVariableChanged = new Subject<number>();

  public getSubProjectDesignVariableChangedSubscribable() {
    return this.subProjectDesignVariableChanged;
  }

  private designAssetsChanged = new Subject<any>();

  public getDesignAssetsChangedSubscribable() {
    return this.designAssetsChanged;
  }

  private invoiceSent = new Subject<any>();

  public getInvoiceSentSubscribable() {
    return this.invoiceSent;
  }

  private productMaterialChanged = new Subject<any>();

  public getProductMaterialChangedSubscribable() {
    return this.productMaterialChanged;
  }

  private productSizeChanged = new Subject<ProductSizeChangedEvent>();

  public getProductSizeChangedSubscribable(): Subject<ProductSizeChangedEvent> {
    return this.productSizeChanged;
  }

  private productSizeChangedNoSideEffect =
    new Subject<ProductSizeChangedEvent>();

  public getProductSizeChangedNoSideEffectSubscribable(): Subject<ProductSizeChangedEvent> {
    return this.productSizeChangedNoSideEffect;
  }

  private productPartTypeChanged = new Subject<any>();

  public getProductPartTypeChangedSubscribable() {
    return this.productPartTypeChanged;
  }

  private standardStickerPartsChanged = new Subject<any>();

  public getStandardStickerPartsChangedSubscribable(): Subject<any> {
    return this.standardStickerPartsChanged;
  }

  public productPartDeleted = new Subject<any>();

  public getProductPartDeletedSubscribable(): Subject<any> {
    return this.productPartDeleted;
  }

  private addressWasHidden = new Subject<boolean>();

  public getAddressWasHiddenSubscribable(): Subject<boolean> {
    return this.addressWasHidden;
  }

  private signSelected = new Subject<SignSelectedEvent>();

  public getSignSelectedSubscribable(): Subject<SignSelectedEvent> {
    return this.signSelected;
  }

  private rightSignSelected = new Subject<SignSelectedEvent>();

  public getRightSignSelectedSubscribable(): Subject<SignSelectedEvent> {
    return this.rightSignSelected;
  }

  private resetLeftRight = new Subject<boolean>();

  public getResetLeftRightSubscribable(): Subject<boolean> {
    return this.resetLeftRight;
  }

  private ipBoardTextFilled = new Subject<boolean>();

  public getIPBoardTextFilledSubscribable(): Subject<boolean> {
    return this.ipBoardTextFilled;
  }

  private ipBoardConfigurationChanged =
    new Subject<IPBoardConfigurationChangedEvent>();

  public getIPBoardConfigurationChangedSubscribable(): Subject<IPBoardConfigurationChangedEvent> {
    return this.ipBoardConfigurationChanged;
  }

  private stockInfoUpdated = new Subject<any>();

  public getStockInfoUpdatedSubscribable(): Subject<any> {
    return this.stockInfoUpdated;
  }

  private subProjectSetOnHold = new Subject<number>();

  public getSubProjectSetOnHoldSubscribable(): Subject<number> {
    return this.subProjectSetOnHold;
  }

  private subProjectStatusChanged = new Subject<SubProjectStatusChangedEvent>();

  public getSubProjectStatusChangedSubscribable(): Subject<SubProjectStatusChangedEvent> {
    return this.subProjectStatusChanged;
  }

  private validStreet = new Subject<any>();

  public getValidStreetSubscribable(): Subject<any> {
    return this.validStreet;
  }

  private validAddress = new Subject<any>();

  public getValidAddressSubscribable(): Subject<any> {
    return this.validAddress;
  }
}

export interface ProductSizeChangedEvent {
  productId: number;
  partId: number;
}

export interface ProductAssetsChangedEvent {
  productId: number;
  formSection?: string;
}

export interface SignSelectedEvent {
  ipBoardPart: IPBoardPart;
  toStandardOrToCustom: boolean;
  adhesiveStockSign: boolean;
  selectedOptionId?: number;
  resetLeftRight?: boolean;
  setAdhesive?: boolean;
}

export interface IPBoardConfigurationChangedEvent {
  ipBoardPart: IPBoardPart;
}

export interface TourConfigurationChangedEvent {
  subProjectId: number;
  assignedToTour: boolean;
  placer: Employee;
  date: Date;
  notExecutedHistoryItem?: NotExecutedHistoryItem;
  locked?: boolean;
}

export interface TourUnassignedAsSideEffectEvent {
  subProjectId: number;
}

export interface NotExecutedHistoryItemAssetsFetchedEvent {
  subProjectId: number;
  assets: NotExecutedHistoryItemAsset[];
}

export interface SubProjectStatusChangedEvent {
  subProjectId: number;
  status: string;
}
